import React, { useState, useEffect, useCallback, useMemo, useRef, startTransition } from 'react';
import NewFeatureChip from '../NewFeatureChip';
import { markFeatureSeen } from '../../utils/newFeature';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  Collapse,
  useTheme,
  alpha,
  Badge,
  Button,
  Popover,
  CircularProgress,
  Chip,
  Stack,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Folder as FolderIcon,
  RequestQuote as ProposalIcon,
  People as PeopleIcon,
  LocalShipping as ShippingIcon,
  Settings as SettingsIcon,
  Shield as ShieldIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Notifications as NotificationsIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
  AdminPanelSettings as AdminPanelIcon,
  Security as SecurityIcon,
  History as HistoryIcon,
  Group as GroupIcon,
  Business as BusinessIcon,
  FileCopy as FileCopyIcon,
  Lock as LockIcon,
  Devices as DevicesIcon,
  Approval as ApprovalIcon,
  Warning as WarningIcon,
  Search as SearchIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Add as AddIcon,
  FiberManualRecord as DotIcon,
  Forum as ForumIcon,
  Inventory as InventoryIcon,
  BarChart as BarChartIcon,
  SmartToyOutlined as SmartToyIcon,
  CalendarMonth as CalendarIcon,
  DeleteOutline as RecycleBinIcon,
  Build as BuildIcon,
  Straighten as StandardsIcon,
  ShoppingCart as ShoppingCartIcon,
  Science as ScienceIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  CameraAlt as CameraIcon,
  Storage as StorageIcon,
  Apps as AppsIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeMode } from '../../contexts/ThemeContext';
import { canSeeSidebarItem, isCoAdmin } from '../../config/rolePermissions';
import { SupervisorAccount as CoAdminIcon } from '@mui/icons-material';
import api from '../../services/api';
import { chatService } from '../../services/chatService';
import GlobalSearch from '../GlobalSearch';
import AIAssistant from '../AIAssistant/AIAssistant';
import { useCompanyLogo, DEFAULT_LOGO } from '../../contexts/CompanyLogoContext';
import { clearActiveCompanyContext, getActiveCompanyContext, listenToActiveCompanyChange } from '../../utils/activeCompany';
import { tokenStorage } from '../../services/tokenStorage';
import PendingSyncIndicator from '../PendingSyncIndicator';
import UpdateAvailableBanner from '../UpdateAvailableBanner';

// Default avatar images (stored in /public/avatars/)
const AVATAR_MALE = '/avatars/male.png';
const AVATAR_FEMALE = '/avatars/female.png';

// ─── Brand Tokens (constant across modes) ────────────────────────────────
const PRIMARY    = '#1F7A63';
const PRIMARY_DK = '#166354';
const PRIMARY_LT = '#2A9D7E';

// ─── Constants ───────────────────────────────────────────────────────────────
const SIDEBAR_EXPANDED = 260;
const SIDEBAR_COLLAPSED = 72;
const HEADER_HEIGHT = 64;
const TRANSITION = '280ms cubic-bezier(0.4, 0, 0.2, 1)';

// Welcome messages that rotate
const WELCOME_MESSAGES = [
  'Welcome aboard! 🚀',
  'Great to see you back! 😊',
  "Let's get things done! 💪",
  "Ready for today's work? 🔥",
  'Good to have you here! 🌟',
  'Making progress together! 🎯',
  "Let's build something great! 🛠️",
  'Glad to have you on the team! 🚀',
];

// ─── Component ───────────────────────────────────────────────────────────────
const Layout: React.FC = () => {
  useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  /* T269 — whatever navigates, the menu goes with the page it was opened on. */
  useEffect(() => { setAppsAnchor(null); }, [location.pathname]);
  // Pages that own a full-bleed sticky header attached to the AppBar.
  // For these routes, the scroll container drops its top padding so the
  // page's sticky header sits flush at y=HEADER_HEIGHT.
  const usesFlushStickyHeader = /^\/(?:projects|proposals)\/[^/]+/.test(location.pathname);
  const { user, logout, refreshUser } = useAuth();
  const { logoUrl: companyLogoUrl } = useCompanyLogo();
  useThemeMode(); // keep provider connection alive
  const [activeCompany, setActiveCompany] = useState(() => getActiveCompanyContext());

  // Welcome message — pick one randomly on mount
  const welcomeMsg = useMemo(() => WELCOME_MESSAGES[Math.floor(Math.random() * WELCOME_MESSAGES.length)], []);

  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('sidebar-collapsed') === 'true'; }
    catch { return false; }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [acOpen, setAcOpen] = useState(() => location.pathname.startsWith('/access-control'));
  const [profileAnchor, setProfileAnchor] = useState<null | HTMLElement>(null);
  const [appsAnchor, setAppsAnchor] = useState<null | HTMLElement>(null);
  const [appsPos, setAppsPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 }); // T267 — see the Popover
  const appsPaperRef = useRef<HTMLDivElement | null>(null);
  const appsButtonRef = useRef<HTMLButtonElement | null>(null);
  /* T269 — second attempt, after the first (backdrop click + document click-away) was
     proven LIVE in the bundle and still did not close it. Both of those run at bubble
     time on `document`, so anything on the page that stops propagation defeats them.
     A CAPTURE-phase listener on `window` runs before any handler on any element can
     stop anything. While the menu is open: any mousedown outside its paper closes it. */
  useEffect(() => {
    if (!appsAnchor) return;
    const onDown = (e: MouseEvent) => {
      const el = appsPaperRef.current;
      const btn = appsButtonRef.current;
      if (e.target instanceof Node && ((el && el.contains(e.target)) || (btn && btn.contains(e.target)))) return;
      setAppsAnchor(null);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setAppsAnchor(null); };
    /* T269 — MEASURED on this laptop's Chrome (1920×911): a real click inside the page
       closes the menu. His words were "click outside the SCREEN" — a click on another
       monitor or another app never reaches the page as a click at all; it arrives as
       the window losing focus. A menu should go with the focus, so blur and the tab
       being hidden close it too. */
    const onAway = () => setAppsAnchor(null);
    window.addEventListener('mousedown', onDown, true);
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('blur', onAway);
    document.addEventListener('visibilitychange', onAway);
    return () => {
      window.removeEventListener('mousedown', onDown, true); window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('blur', onAway); document.removeEventListener('visibilitychange', onAway);
    };
  }, [appsAnchor]);
  /* T249 — PARKED ON HIS RULING, NOT DELETED. Owner (F30): "remove the ai chat
     and normal chat from the ui header, keep it parked in the code side, dont
     delete it, just remove the icoon from the ui" — the client does not use
     either, and the Railway ai-service was costing money for nobody.
     Every component, route, service and handler below stays exactly where it
     is; ONLY the two header icons are withheld. Flip this ONE constant back to
     true to restore both, with no other edit anywhere. */
  const SHOW_CHAT_AND_AI_ICONS = false;
  const [aiOpen, setAiOpen] = useState(false);

  // 3-dot menu state
  const [dotMenuAnchor, setDotMenuAnchor] = useState<null | HTMLElement>(null);
  const [editNameOpen, setEditNameOpen] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');
  const [avatarUploading, setAvatarUploading] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  // Notification state
  const [notifAnchor, setNotifAnchor] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Chat unread count
  const [chatUnread, setChatUnread] = useState(0);

  const fetchChatUnread = useCallback(async () => {
    try {
      const count = await chatService.getUnreadCount();
      setChatUnread(count);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchChatUnread();
    const interval = setInterval(fetchChatUnread, 10000);
    return () => clearInterval(interval);
  }, [fetchChatUnread]);



  const drawerWidth = collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED;

  const isMainAdmin = user?.role === 'main_admin';
  const isAdmin = user?.role === 'admin';
  const isAdminPlus = isMainAdmin || isAdmin;
  const userIsCoAdmin = user?.is_co_admin ?? isCoAdmin(user?.role, user?.is_co_admin);
  const isAcPath = location.pathname.startsWith('/access-control');
  const isPlatformAdminInCompany = user?.role === 'platform_admin' && !!activeCompany;

  // RBAC visibility helpers
  const showAdministration = userIsCoAdmin || isMainAdmin; // Owner/Co-Owner/Super Admin
  const showEnterprise = user?.role === 'platform_admin'; // Platform Admin only — hidden for all company users
  const showEnterpriseItem = (path: string) => canSeeSidebarItem(user?.role, path, userIsCoAdmin);

  useEffect(() => { if (isAcPath) setAcOpen(true); }, [isAcPath]);

  useEffect(() => {
    const syncActiveCompany = () => setActiveCompany(getActiveCompanyContext());
    return listenToActiveCompanyChange(syncActiveCompany);
  }, []);

  const toggleCollapse = () => {
    setCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('sidebar-collapsed', String(next)); } catch {}
      return next;
    });
  };

  const handleNav = (path: string) => {
    /* T327 — *"it should be removed when the user click on it"*. Opening the thing is the only
       dismissal there is: no settings switch, no "mark all read". */
    markFeatureSeen(path, user?.id);
    navigate(path);
    setMobileOpen(false);
  };
  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleProfileOpen = (e: React.MouseEvent<HTMLElement>) => setProfileAnchor(e.currentTarget);
  const handleProfileClose = () => setProfileAnchor(null);
  const handleLogout = () => { handleProfileClose(); logout(); navigate('/login'); };
  const handleExitCompany = () => {
    clearActiveCompanyContext();
    // Restore platform admin token if impersonating. On desktop the token
    // sits in OS-secure storage (Step 6.3), so route through tokenStorage
    // instead of writing plain localStorage.
    const savedToken = tokenStorage.get('platform_admin_token');
    const savedUser = localStorage.getItem('platform_admin_user');
    if (savedToken) {
      void tokenStorage.set('token', savedToken, 'local');
      void tokenStorage.remove('platform_admin_token');
    }
    if (savedUser) {
      localStorage.setItem('user', savedUser);
      localStorage.removeItem('platform_admin_user');
    }
    navigate('/platform-admin');
  };

  // 3-dot menu handlers
  const handleDotMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setDotMenuAnchor(e.currentTarget);
  };
  const handleDotMenuClose = () => setDotMenuAnchor(null);

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarUploading(true);
    try {
      const fd = new FormData();
      fd.append('avatar', file);
      await api.post('/users/avatar', fd);
      await refreshUser();
    } catch (err) {
      console.error('Avatar upload failed:', err);
    } finally {
      setAvatarUploading(false);
      handleDotMenuClose();
    }
  };

  const handleUpdateName = async () => {
    const trimmed = editNameValue.trim();
    if (!trimmed || trimmed === user?.name) { setEditNameOpen(false); return; }
    try {
      await api.put('/users/profile', { name: trimmed });
      await refreshUser();
    } catch (err) {
      console.error('Name update failed:', err);
    } finally {
      setEditNameOpen(false);
      handleDotMenuClose();
    }
  };

  // Helper: get avatar src with gender-aware fallback
  const getAvatarSrc = (avatar?: string | null) => {
    if (avatar) return avatar;
    const gender = (user as any)?.gender?.toLowerCase();
    if (gender === 'male') return AVATAR_MALE;
    if (gender === 'female') return AVATAR_FEMALE;
    return undefined; // blank — no image until gender selected or DP uploaded
  };

  // Notification handlers
  const fetchNotifications = useCallback(async () => {
    setNotifLoading(true);
    try {
      const res = await api.get('/audit-logs', { params: { page: 1, limit: 8 } });
      const logs = res.data?.logs || res.data?.data || (Array.isArray(res.data) ? res.data : []);
      setNotifications(logs.slice(0, 8));
      setUnreadCount(Math.min(logs.length, 8));
    } catch {
      setNotifications([]);
    } finally {
      setNotifLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleNotifOpen = (e: React.MouseEvent<HTMLElement>) => {
    setNotifAnchor(e.currentTarget);
    setUnreadCount(0);
  };
  const handleNotifClose = () => setNotifAnchor(null);

  const getNotifIcon = (action: string) => {
    if (action?.includes('create')) return { icon: <AddIcon sx={{ fontSize: 16 }} />, color: '#10B981' };
    if (action?.includes('update') || action?.includes('edit')) return { icon: <HistoryIcon sx={{ fontSize: 16 }} />, color: '#3B82F6' };
    if (action?.includes('delete')) return { icon: <WarningIcon sx={{ fontSize: 16 }} />, color: '#EF4444' };
    if (action?.includes('login')) return { icon: <PersonIcon sx={{ fontSize: 16 }} />, color: '#2A9D7E' };
    return { icon: <DotIcon sx={{ fontSize: 16 }} />, color: '#94A3B8' };
  };

  const formatTimeAgo = (date: string) => {
    const diff = Date.now() - new Date(date).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  // ─── Sidebar Nav Item ───────────────────────────────────────────────────────
  const renderNavItem = (icon: React.ReactNode, label: string, path: string, isCollapsedView: boolean) => {
    const active = isActive(path);
    const button = (
      <ListItemButton
        onClick={() => handleNav(path)}
        sx={{
          minHeight: 40,
          borderRadius: '8px',
          mx: isCollapsedView ? 0.75 : 0,
          px: isCollapsedView ? 0 : 1.0,
          py: 0.6,
          justifyContent: isCollapsedView ? 'center' : 'flex-start',
          transition: 'all 0.22s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          ...(active
            ? {
                bgcolor: alpha(PRIMARY, 0.07),
                color: PRIMARY,
                boxShadow: '0 1px 4px rgba(31, 122, 99, 0.10)',
                borderLeft: isCollapsedView ? 'none' : `3px solid ${PRIMARY}`,
                '&::before': isCollapsedView ? {
                  content: '""',
                  position: 'absolute',
                  left: '50%',
                  top: 0,
                  width: '60%',
                  height: 3,
                  transform: 'translateX(-50%)',
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #1F7A63, #2A9D7E)',
                } : undefined,
                '&:hover': { bgcolor: alpha(PRIMARY, 0.11) },
              }
            : {
                color: 'var(--text-secondary)',
                opacity: 0.82,
                '&:hover': {
                  bgcolor: 'var(--bg-sidebar-active)',
                  color: 'var(--text-primary)',
                  opacity: 1,
                  transform: isCollapsedView ? 'none' : 'translateX(3px)',
                },
              }),
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: isCollapsedView ? 0 : 34,
            justifyContent: 'center',
            color: active ? PRIMARY : 'var(--text-muted)',
            transition: 'color 0.18s ease',
            '& .MuiSvgIcon-root': { fontSize: 19 },
          }}
        >
          {icon}
        </ListItemIcon>
        {!isCollapsedView && (
          <ListItemText
            primary={label}
            primaryTypographyProps={{
              fontSize: '0.82rem',
              fontWeight: active ? 650 : 450,
              color: active ? PRIMARY : 'var(--text-primary)',
              whiteSpace: 'nowrap',
              letterSpacing: '-0.01em',
            }}
            sx={{ '& .MuiListItemText-primary': { opacity: active ? 1 : 0.88 } }}
          />
        )}
        {/* T327 — collapsed rail has no room for a chip and no room for a word either; the
            badge simply does not apply there. */}
        {!isCollapsedView && <NewFeatureChip path={path} userKey={user?.id} />}

      </ListItemButton>
    );

    return (
      <ListItem disablePadding sx={{ mb: 0.3 }} key={path}>
        {isCollapsedView ? <Tooltip title={label} placement="right" arrow>{button}</Tooltip> : button}
      </ListItem>
    );
  };

  const renderSectionLabel = (label: string, isCollapsedView: boolean) => {
    if (isCollapsedView) return <Divider sx={{ my: 2.5, mx: 1.5, borderColor: 'var(--border)' }} />;
    return (
      <Box sx={{ px: 1.0, pt: 3, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.12em', whiteSpace: 'nowrap' }}>
          {label}
        </Typography>
        <Box sx={{ flex: 1, height: '1px', background: 'linear-gradient(90deg, var(--border) 0%, transparent 100%)' }} />
      </Box>
    );
  };

  const renderAccessControl = (isCollapsedView: boolean) => {
    const acItems = [
      { icon: <CoAdminIcon />, label: 'Co Admins', path: '/access-control/co-admins', show: userIsCoAdmin || isMainAdmin },
      { icon: <AdminPanelIcon />, label: 'Admins', path: '/access-control/admins', show: isMainAdmin },
      { icon: <GroupIcon />, label: 'Users', path: '/access-control/users', show: true },
      { icon: <BusinessIcon />, label: 'Companies', path: '/access-control/companies', show: isMainAdmin },
      { icon: <FileCopyIcon />, label: 'Templates', path: '/access-control/templates', show: isAdminPlus },
      { icon: <SecurityIcon />, label: 'Roles & Permissions', path: '/access-control/roles', show: isAdminPlus },
      { icon: <LockIcon />, label: 'Security', path: '/access-control/security', show: isAdminPlus },
      { icon: <HistoryIcon />, label: 'Audit Logs', path: '/access-control/audit-logs', show: isAdminPlus },
    ].filter(item => item.show);

    if (isCollapsedView) {
      return (
        <ListItem disablePadding sx={{ mb: 0.5 }}>
          <Tooltip title="Access Control" placement="right" arrow>
            <ListItemButton
              onClick={() => { toggleCollapse(); setAcOpen(true); handleNav('/access-control/users'); }}
              sx={{
                minHeight: 44, borderRadius: '8px', mx: 0.75, justifyContent: 'center',
                transition: 'all 0.15s ease',
                ...(isAcPath
                  ? { bgcolor: PRIMARY, color: '#fff', '&:hover': { bgcolor: PRIMARY_DK } }
                  : { '&:hover': { bgcolor: 'var(--bg-sidebar-active)' } }),
              }}
            >
              <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center', color: isAcPath ? '#fff' : 'var(--text-muted)', '& .MuiSvgIcon-root': { fontSize: 20 } }}>
                <ShieldIcon />
              </ListItemIcon>
            </ListItemButton>
          </Tooltip>
        </ListItem>
      );
    }

    return (
      <>
        <ListItem disablePadding sx={{ mb: 0.5 }}>
          <ListItemButton
            onClick={() => setAcOpen(!acOpen)}
            sx={{
              minHeight: 44, borderRadius: '8px', px: 1.5, py: 0.8,
              transition: 'all 0.15s ease',
              ...(isAcPath && !acOpen ? { bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DK } } : { '&:hover': { bgcolor: 'var(--bg-sidebar-active)' } }),
            }}
          >
            <ListItemIcon sx={{ minWidth: 36, color: isAcPath ? (acOpen ? PRIMARY : '#fff') : 'var(--text-muted)', '& .MuiSvgIcon-root': { fontSize: 20 } }}>
              <ShieldIcon />
            </ListItemIcon>
            <ListItemText primary="Access Control" primaryTypographyProps={{ fontSize: '0.855rem', fontWeight: isAcPath ? 600 : 500, color: isAcPath ? (acOpen ? PRIMARY : '#fff') : 'var(--text-primary)', whiteSpace: 'nowrap', letterSpacing: '-0.01em' }} />
            <ExpandMoreIcon sx={{ fontSize: 18, color: 'var(--text-muted)', transition: 'transform 0.2s ease', transform: acOpen ? 'rotate(180deg)' : 'rotate(0)' }} />
          </ListItemButton>
        </ListItem>
        <Collapse in={acOpen} timeout="auto">
          <List disablePadding sx={{ pl: 0.5, pb: 0.5, position: 'relative', '&::before': { content: '""', position: 'absolute', left: 26, top: 0, bottom: 8, width: '1.5px', bgcolor: 'var(--border)', borderRadius: 1 } }}>
            {acItems.map(item => {
              const active = location.pathname === item.path;
              return (
                <ListItem key={item.path} disablePadding sx={{ mb: 0.1 }}>
                  <ListItemButton
                    onClick={() => handleNav(item.path)}
                    sx={{
                      minHeight: 34, borderRadius: '8px', py: 0.35, ml: 1.5, px: 1.5,
                      transition: 'all 0.2s ease',
                      borderLeft: `2px solid ${active ? PRIMARY : 'transparent'}`,
                      ...(active ? { bgcolor: alpha(PRIMARY, 0.06), boxShadow: '0 0 0 1px rgba(31,122,99,0.06)' } : { opacity: 0.78, '&:hover': { bgcolor: 'var(--bg-sidebar-active)', opacity: 1 } }),
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 26, color: active ? PRIMARY : 'var(--text-muted)', '& .MuiSvgIcon-root': { fontSize: 15 } }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '0.78rem', fontWeight: active ? 650 : 400, color: active ? PRIMARY : 'var(--text-secondary)' }} />

                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Collapse>
      </>
    );
  };

  // ─── Drawer Content ─────────────────────────────────────────────────────────
  const drawerContent = (isMobile: boolean) => {
    const c = !isMobile && collapsed;

    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'var(--bg-sidebar)', borderRight: '1px solid var(--border)' }}>
        {/* Logo & Toggle */}
        <Box sx={{ p: c ? 1.5 : 2, display: 'flex', alignItems: 'center', justifyContent: c ? 'center' : 'space-between', minHeight: HEADER_HEIGHT }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, overflow: 'hidden', cursor: 'pointer', transition: 'opacity 0.2s ease', '&:hover': { opacity: 0.8 } }} onClick={() => handleNav('/')}>
            <Box sx={{
              width: 36, height: 36, borderRadius: '8px',
              background: 'linear-gradient(135deg, #1F7A63 0%, #166354 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
              boxShadow: '0 2px 8px rgba(31, 122, 99, 0.25)',
            }}>
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em' }}>FH</Typography>
            </Box>
            {!c && (
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif', fontWeight: 700, color: 'var(--text-primary)', fontSize: '0.95rem', letterSpacing: '-0.02em', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                  Forge Halima
                </Typography>
                <Typography sx={{ fontSize: '0.58rem', color: 'var(--text-muted)', letterSpacing: '0.08em', whiteSpace: 'nowrap', textTransform: 'uppercase', fontWeight: 600 }}>
                  Industrial Systems
                </Typography>
              </Box>
            )}
          </Box>
          {!isMobile && !c && (
            <Tooltip title="Collapse sidebar" placement="right">
              <IconButton onClick={toggleCollapse} size="small" sx={{ color: 'var(--text-muted)', transition: 'all 0.2s ease', '&:hover': { bgcolor: 'var(--bg-sidebar-active)', color: PRIMARY } }}>
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Divider sx={{ mx: c ? 1 : 2, borderColor: 'var(--border-subtle)' }} />

        {/* Navigation */}
        <Box sx={{ flex: 1, overflow: 'auto', py: 1, px: c ? 0.5 : 0.5 }}>
          {/* OPERATIONS */}
          {renderSectionLabel('Operations', c)}
          <List disablePadding>
            {/* T99 — the dashboard moved to its own path when Projects became the
                landing page. The link has to follow it, or this item navigates to
                "/" and the redirect bounces the user straight back to Projects,
                making the dashboard unreachable from the UI. */}
            {renderNavItem(<DashboardIcon />, 'Dashboard', '/dashboard', c)}
            {renderNavItem(<ProposalIcon />, 'Proposals', '/proposals', c)}
            {renderNavItem(<FolderIcon />, 'Projects', '/projects', c)}
            {canSeeSidebarItem(user?.role, '/procurement', userIsCoAdmin) && (
              <ListItem disablePadding sx={{ mb: 0.3 }}>
                {c ? (
                  <Tooltip title="Procurement" placement="right" arrow>
                    <ListItemButton
                      onClick={() => handleNav('/procurement')}
                      sx={{
                        minHeight: 40,
                        borderRadius: '8px',
                        mx: 0.75,
                        justifyContent: 'center',
                        transition: 'all 0.22s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        ...(isActive('/procurement')
                          ? {
                              bgcolor: alpha(PRIMARY, 0.07),
                              color: PRIMARY,
                              boxShadow: '0 1px 4px rgba(31, 122, 99, 0.10)',
                              '&::before': {
                                content: '""',
                                position: 'absolute',
                                left: '50%',
                                top: 0,
                                width: '60%',
                                height: 3,
                                transform: 'translateX(-50%)',
                                borderRadius: 2,
                                background: 'linear-gradient(135deg, #1F7A63, #2A9D7E)',
                              },
                              '&:hover': { bgcolor: alpha(PRIMARY, 0.11) },
                            }
                          : {
                              color: 'var(--text-secondary)',
                              opacity: 0.82,
                              '&:hover': { bgcolor: 'var(--bg-sidebar-active)', color: 'var(--text-primary)', opacity: 1 },
                            }),
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center', color: isActive('/procurement') ? PRIMARY : 'var(--text-muted)', '& .MuiSvgIcon-root': { fontSize: 19 } }}>
                        <ShoppingCartIcon />
                      </ListItemIcon>
                    </ListItemButton>
                  </Tooltip>
                ) : (
                  <ListItemButton
                    onClick={() => handleNav('/procurement')}
                    sx={{
                      minHeight: 40,
                      borderRadius: '8px',
                      px: 1.0,
                      py: 0.6,
                      transition: 'all 0.22s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative',
                      ...(isActive('/procurement')
                        ? {
                            bgcolor: alpha(PRIMARY, 0.07),
                            color: PRIMARY,
                            boxShadow: '0 1px 4px rgba(31, 122, 99, 0.10)',
                            borderLeft: `3px solid ${PRIMARY}`,
                            '&:hover': { bgcolor: alpha(PRIMARY, 0.11) },
                          }
                        : {
                            color: 'var(--text-secondary)',
                            opacity: 0.82,
                            '&:hover': { bgcolor: 'var(--bg-sidebar-active)', color: 'var(--text-primary)', opacity: 1, transform: 'translateX(3px)' },
                          }),
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 34, justifyContent: 'center', color: isActive('/procurement') ? PRIMARY : 'var(--text-muted)', '& .MuiSvgIcon-root': { fontSize: 19 } }}>
                      <ShoppingCartIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Procurement"
                      primaryTypographyProps={{
                        fontSize: '0.82rem',
                        fontWeight: isActive('/procurement') ? 650 : 450,
                        color: isActive('/procurement') ? PRIMARY : 'var(--text-primary)',
                        whiteSpace: 'nowrap',
                        letterSpacing: '-0.01em',
                      }}
                    />
                    {/* T327 — the HARDCODED "New" that lived here came off. A badge with no
                        expiry and no memory is decoration, and it had been shouting at him for
                        months. Procurement now gets a chip only if `NEW_FEATURES` says so, like
                        everything else, and it goes the moment he clicks it. */}
                    <NewFeatureChip path="/procurement" userKey={user?.id} />
                  </ListItemButton>
                )}
              </ListItem>
            )}
            {canSeeSidebarItem(user?.role, '/material-stock', userIsCoAdmin) && renderNavItem(<InventoryIcon />, 'Inventory', '/material-stock', c)}
            {renderNavItem(<FolderIcon />, 'File Manager', '/file-manager', c)}
          </List>
        </Box>

        {/* Bottom */}
        <Box sx={{ px: c ? 0.5 : 1.25, pb: 0.5 }}>
        </Box>

        {!isMobile && c && (
          <Box sx={{ pb: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            {companyLogoUrl && (
              <Box component="img" src={companyLogoUrl} alt="Company" onError={(e: any) => { e.target.style.display = 'none'; }} sx={{ maxWidth: 48, maxHeight: 48, width: 'auto', height: 'auto', borderRadius: '8px', objectFit: 'contain', opacity: 0.9 }} />
            )}
            <Tooltip title="Expand sidebar" placement="right">
              <IconButton onClick={toggleCollapse} size="small" sx={{ color: 'var(--text-muted)', '&:hover': { bgcolor: 'var(--bg-sidebar-active)', color: PRIMARY } }}>
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {!c && (
          <Box sx={{ pb: 3, pt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', px: 1 }}>
            {companyLogoUrl && (
              <Box component="img" src={companyLogoUrl} alt="Company Logo" onError={(e: any) => { e.target.style.display = 'none'; }} sx={{ maxWidth: '85%', width: 'auto', height: 'auto', maxHeight: 64, objectFit: 'contain', opacity: 0.9, borderRadius: '8px' }} />
            )}
            <Typography sx={{ color: 'var(--text-muted)', fontSize: '0.6rem', mt: 0.8, fontWeight: 500, letterSpacing: '0.02em' }}>v1.0.0 &copy; {new Date().getFullYear()}</Typography>
          </Box>
        )}
      </Box>
    );
  };

  // ─── Layout Return ──────────────────────────────────────────────────────────
  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden', bgcolor: 'var(--bg-canvas)', transition: 'background-color 0.3s ease' }}>
      {/* Update card — floats in the sidebar's empty space above the company
          logo (never over the header/logo). Hidden on mobile; renders nothing
          on web. Width tracks the sidebar so it sits neatly inside the rail. */}
      <Box
        sx={{
          display: { xs: 'none', md: 'block' },
          position: 'fixed',
          bottom: 148,
          left: 10,
          width: Math.max(drawerWidth - 20, 220),
          zIndex: 1300,
          pointerEvents: 'auto',
          transition: `left ${TRANSITION}, width ${TRANSITION}`,
        }}
      >
        <UpdateAvailableBanner />
      </Box>
      {/* Header */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          transition: `width ${TRANSITION}, margin-left ${TRANSITION}, background-color 0.3s ease`,
          bgcolor: 'rgba(255, 255, 255, 0.92)',
          backdropFilter: 'blur(24px) saturate(200%)',
          borderBottom: '1px solid',
          borderColor: 'var(--border-subtle)',
          height: HEADER_HEIGHT,
          zIndex: 1100,
        }}
      >
        <Toolbar sx={{ px: { xs: 2, sm: 3 }, minHeight: `${HEADER_HEIGHT}px !important`, height: HEADER_HEIGHT }}>
          <IconButton edge="start" onClick={() => setMobileOpen(!mobileOpen)} sx={{ mr: 2, display: { md: 'none' }, color: 'var(--text-primary)' }}>
            <MenuIcon />
          </IconButton>

          {/* Search Bar */}
          <GlobalSearch />

          <Box sx={{ flexGrow: 1 }} />

          {isPlatformAdminInCompany && activeCompany && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mr: 1.5, display: { xs: 'flex' }, maxWidth: { xs: 180, sm: 'none' } }}>
              <Chip
                size="small"
                label={`Company: ${activeCompany.name}`}
                sx={{ bgcolor: alpha(PRIMARY, 0.08), color: PRIMARY, fontWeight: 700, maxWidth: 220 }}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={handleExitCompany}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}
              >
                Exit Company
              </Button>
            </Stack>
          )}

          {/* Pending sync (offline action queue) — desktop only */}
          <PendingSyncIndicator />

          {/* Messages — parked, T249 */}
          {SHOW_CHAT_AND_AI_ICONS && (
          <Tooltip title="Messages">
            <IconButton onClick={() => navigate('/messages')} sx={{ color: 'var(--text-secondary)', ml: 1, transition: 'all 0.15s ease', '&:hover': { bgcolor: 'var(--bg-sidebar-active)', color: PRIMARY } }}>
              <Badge badgeContent={chatUnread || undefined} color="error" sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', height: 18, minWidth: 18, fontWeight: 700 } }}>
                <ForumIcon sx={{ fontSize: 21 }} />
              </Badge>
            </IconButton>
          </Tooltip>
          )}

          {/* Notifications */}
          <Tooltip title="Notifications">
            <IconButton onClick={handleNotifOpen} sx={{ color: 'var(--text-secondary)', ml: 1, position: 'relative', transition: 'all 0.15s ease', '&:hover': { bgcolor: 'var(--bg-sidebar-active)', color: PRIMARY } }}>
              <Badge badgeContent={unreadCount || undefined} color="error" sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.65rem', height: 18, minWidth: 18, fontWeight: 700,
                  ...(unreadCount > 0 ? {
                    animation: 'pulse-badge 2s ease-in-out infinite',
                    '@keyframes pulse-badge': {
                      '0%, 100%': { transform: 'scale(1) translate(50%, -50%)', boxShadow: '0 0 0 0 rgba(239,68,68,0.4)' },
                      '50%': { transform: 'scale(1.1) translate(50%, -50%)', boxShadow: '0 0 0 6px rgba(239,68,68,0)' },
                    },
                  } : {}),
                },
              }}>
                <NotificationsIcon sx={{ fontSize: 21 }} />
              </Badge>
              {unreadCount > 0 && (
                <Box sx={{ position: 'absolute', top: 8, right: 8, width: 7, height: 7, borderRadius: '50%', bgcolor: '#EF4444', border: '1.5px solid #fff', zIndex: 2 }} />
              )}
            </IconButton>
          </Tooltip>

          {/* Notification Popover */}
          <Popover
            open={Boolean(notifAnchor)}
            anchorEl={notifAnchor}
            onClose={handleNotifClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { mt: 1.5, width: 380, maxHeight: 480, borderRadius: '10px', boxShadow: '0 20px 60px -12px rgba(0,0,0,0.15)', border: '1px solid var(--border)', overflow: 'hidden' } }}
          >
            <Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-surface-2)' }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>Notifications</Typography>
              <Chip label={`${notifications.length} recent`} size="small" sx={{ bgcolor: 'rgba(31, 122, 99, 0.06)', color: PRIMARY, fontWeight: 600, fontSize: '0.7rem', height: 22, borderRadius: '6px' }} />
            </Box>
            <Box sx={{ maxHeight: 340, overflow: 'auto' }}>
              {notifLoading ? (
                <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress size={24} sx={{ color: PRIMARY }} /></Box>
              ) : notifications.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <NotificationsIcon sx={{ fontSize: 36, color: 'var(--border)', mb: 1 }} />
                  <Typography sx={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>No recent activity</Typography>
                </Box>
              ) : (
                notifications.map((n, i) => {
                  const { icon, color } = getNotifIcon(n.action);
                  return (
                    <Box key={n.id || i} sx={{ px: 2.5, py: 1.5, display: 'flex', gap: 1.5, alignItems: 'flex-start', borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer', transition: 'background 0.15s', '&:hover': { bgcolor: 'var(--bg-surface-2)' } }}>
                      <Box sx={{ mt: 0.3, width: 30, height: 30, borderRadius: '8px', bgcolor: alpha(color, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color }}>
                        {icon}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: 'var(--text-primary)', lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {n.action?.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()) || 'Activity'}
                        </Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: 'var(--text-muted)', mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {n.entity_name || (typeof n.details === 'string' ? n.details : n.entity_type) || 'System activity'}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: '0.68rem', color: 'var(--text-muted)', whiteSpace: 'nowrap', flexShrink: 0, mt: 0.3 }}>
                        {formatTimeAgo(n.created_at || n.timestamp)}
                      </Typography>
                    </Box>
                  );
                })
              )}
            </Box>
            <Box sx={{ px: 2.5, py: 1.5, borderTop: '1px solid var(--border-subtle)', textAlign: 'center' }}>
              <Button size="small" onClick={() => { handleNotifClose(); navigate('/activity-timeline'); }} sx={{ textTransform: 'none', color: PRIMARY, fontWeight: 600, fontSize: '0.8rem', '&:hover': { bgcolor: alpha(PRIMARY, 0.06) } }}>
                View All Activity
              </Button>
            </Box>
          </Popover>

          {/* Amber AI — parked, T249 */}
          {SHOW_CHAT_AND_AI_ICONS && (
          <Tooltip title="Amber AI">
            <IconButton onClick={() => setAiOpen(true)} sx={{ color: 'var(--text-secondary)', ml: 1, transition: 'all 0.15s ease', '&:hover': { bgcolor: 'var(--bg-sidebar-active)', color: PRIMARY } }}>
              <SmartToyIcon sx={{ fontSize: 21 }} />
            </IconButton>
          </Tooltip>
          )}

          {/* T214 — Analytics and Calendar USED TO SIT HERE as their own top-bar
              buttons. Both now live in the Menu below, his ruling. They are DELETED
              rather than hidden: a control left behind a falsy guard keeps every
              string in the bundle and reads as still-shipped to the next reader
              (mistake shape 39). */}

          {/* Apps grid menu */}
          <Tooltip title="Menu">
            <IconButton ref={appsButtonRef} onClick={(e) => {
              /* T269 — read the element NOW: React clears `currentTarget` once the handler
                 returns, so reading it inside the deferred updater yielded null and the
                 menu could never open (his words: "now the menu button is not working"). */
              const target = e.currentTarget; const r = target.getBoundingClientRect();
              setAppsPos({ top: r.bottom + 8, left: r.right });
              setAppsAnchor(prev => (prev ? null : target));
            }} sx={{ color: 'var(--text-secondary)', ml: 0.5, transition: 'all 0.15s ease', '&:hover': { bgcolor: 'var(--bg-sidebar-active)', color: PRIMARY } }}>
              <AppsIcon sx={{ fontSize: 21 }} />
            </IconButton>
          </Tooltip>

          {/* T267 — his words: *"the menu ... being placed in bottom right corner of the page
              instead of near the menu button"*. MUI clamps a popover to the viewport's
              bottom-right when the position it derives from `anchorEl` is off-screen — i.e.
              when the anchor is measured wrong AFTER the click (re-rendered, detached, or laid
              out inside something that moved). The position is now taken from the button's
              own rectangle AT THE CLICK and handed over as a fixed point, so nothing that
              happens afterwards can move it. `anchorEl` is kept only for focus return. */}
          <Popover
            open={Boolean(appsAnchor)}
            anchorEl={appsAnchor}
            anchorReference="anchorPosition"
            anchorPosition={appsPos}
            onClose={() => setAppsAnchor(null)}
            /* T269 — his words: *"when i click outside the screen its not disappearing"*.
               `onClose` is MUI's backdrop-click path; the desktop app was not closing on
               it. Belt and braces: the backdrop's own click AND a document-level
               click-away both close it, so whichever path the click takes, it closes. */
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { mt: 1.5, width: 320, borderRadius: '10px', boxShadow: '0 20px 60px -12px rgba(0,0,0,0.15)', border: '1px solid var(--border)', overflow: 'hidden' } }}
          >
            <Box ref={appsPaperRef}>
            <Box sx={{ px: 2, pt: 1.75, pb: 1 }}>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.08em', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Menu</Typography>
            </Box>
            <Box sx={{ px: 1.5, pb: 2, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.5 }}>
              {[
                { label: 'Raw Materials', icon: <InventoryIcon />, color: '#0EA5E9', to: '/raw-materials' },
                { label: 'Parts Master', icon: <BuildIcon />,    color: '#8B5CF6', to: '/parts-master' },
                { label: 'Clients',    icon: <PeopleIcon />,     color: '#10B981', to: '/clients' },
                { label: 'Vendors',    icon: <ShippingIcon />,   color: '#3B82F6', to: '/vendors' },
                /* T292 — his words: "lets have one more menu item page called standards". */
                { label: 'Standards',  icon: <StandardsIcon />,  color: '#0D9488', to: '/standards' },
                /* T214 — moved OFF the top bar and into this menu, with Calendar,
                   which was already here. The top bar keeps only what is used
                   constantly; everything navigational lives in one place. */
                { label: 'Analytics',  icon: <BarChartIcon />,   color: '#6366F1', to: '/business-analytics' },
                { label: 'File Manager', icon: <FolderIcon />,   color: '#EC4899', to: '/file-manager' },
                { label: 'Calendar',   icon: <CalendarIcon />,   color: '#F59E0B', to: '/activity-timeline?view=calendar' },
                { label: 'Settings',   icon: <SettingsIcon />,   color: '#64748b', to: '/settings' },
              ].map((it) => (
                <Box key={it.label}
                  /* T269 — MEASURED (his Chrome, 2026-09-09): the close and the navigation used
                     to share one batch; the navigation SUSPENDS on the lazy page chunk (T262),
                     so React held the whole batch — menu close included — until the chunk
                     arrived. On a slow fetch the menu "stayed"; on a chunk that no longer
                     exists after a redeploy it stayed forever. Close synchronously FIRST, and
                     let the navigation be a transition, which keeps the current page on
                     screen until the next one is ready instead of freezing everything. */
                  onClick={() => { setAppsAnchor(null); startTransition(() => navigate(it.to)); }}
                  sx={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75,
                    py: 1.5, px: 0.5, borderRadius: '8px', cursor: 'pointer',
                    transition: 'background 0.15s ease',
                    '&:hover': { bgcolor: 'var(--bg-surface-2)' },
                  }}>
                  <Box sx={{
                    width: 46, height: 46, borderRadius: '8px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: alpha(it.color, 0.12), color: it.color,
                    '& svg': { fontSize: 24 },
                  }}>
                    {it.icon}
                  </Box>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-primary)', textAlign: 'center', lineHeight: 1.2 }}>
                    {it.label}
                  </Typography>
                </Box>
              ))}
            </Box>
            </Box>
          </Popover>

          <Divider orientation="vertical" flexItem sx={{ mx: 1.5, my: 1.5, borderColor: 'var(--border)' }} />

          {/* Profile */}
          <Box onClick={handleProfileOpen} sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 0.5, pr: 1, py: 0.5, borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s ease', '&:hover': { bgcolor: 'var(--bg-sidebar-active)' } }}>
            <Avatar src={getAvatarSrc(user?.avatar)} sx={{ width: 34, height: 34, bgcolor: '#e2e8f0', border: '2px solid rgba(31, 122, 99, 0.15)' }} />
            <Box sx={{ display: { xs: 'none', sm: 'block' }, maxWidth: 120, overflow: 'hidden' }}>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name}</Typography>
              <Typography sx={{ fontSize: '0.68rem', color: 'var(--text-muted)', textTransform: 'capitalize', lineHeight: 1.2 }}>
                {user?.is_owner ? 'Owner' : user?.role === 'main_admin' ? 'Admin' : user?.role === 'admin' ? 'Admin' : user?.role === 'sales_engineer' ? 'Sales Engineer' : user?.role}
              </Typography>
            </Box>
            <ArrowDownIcon sx={{ fontSize: 16, color: 'var(--text-muted)', display: { xs: 'none', sm: 'block' }, transition: 'transform 0.2s', transform: profileAnchor ? 'rotate(180deg)' : 'rotate(0)' }} />
          </Box>

          {/* Hidden avatar file input */}
          <input ref={avatarInputRef} type="file" hidden accept="image/*" onChange={handleAvatarUpload} />

          {/* Profile Menu */}
          <Menu anchorEl={profileAnchor} open={Boolean(profileAnchor)} onClose={handleProfileClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { mt: 1, minWidth: 320, maxWidth: 380, borderRadius: '10px', boxShadow: '0 20px 60px -12px rgba(0,0,0,0.25)', border: '1px solid', borderColor: 'var(--border)', overflow: 'hidden', bgcolor: 'var(--bg-surface)', p: 0 } }}>

            {/* Welcome Card Header */}
            <Box sx={{ background: 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #1e293b 100%)', px: 2.5, pt: 2.5, pb: 2, position: 'relative', overflow: 'hidden' }}>
              {/* 3-dot menu button */}
              <IconButton onClick={handleDotMenuOpen} sx={{ position: 'absolute', top: 8, right: 8, color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' } }}>
                <MoreVertIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar src={getAvatarSrc(user?.avatar)} sx={{ width: 52, height: 52, bgcolor: '#e2e8f0', border: '3px solid rgba(255,255,255,0.15)' }} />
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#fff', lineHeight: 1.3 }}>
                    Welcome back, {user?.name?.split(' ')[0]} 👋
                  </Typography>
                  <Chip
                    icon={<Box sx={{ fontSize: '0.7rem', mr: -0.5 }}>👑</Box>}
                    label={user?.is_owner ? 'Owner' : user?.role === 'main_admin' ? 'Admin' : user?.role === 'admin' ? 'Admin' : user?.role === 'sales_engineer' ? 'Sales Engineer' : 'Member'}
                    size="small"
                    sx={{ mt: 0.5, height: 22, fontSize: '0.7rem', fontWeight: 700, bgcolor: '#d4a017', color: '#fff', borderRadius: '6px', '& .MuiChip-icon': { ml: 0.5 } }}
                  />
                </Box>
              </Box>

              {/* User details bar */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.5, pt: 1.5, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PersonIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.5)' }} />
                  <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.7)' }}>@{user?.name?.toLowerCase().replace(/\s+/g, '')}</Typography>
                </Box>
                <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.15)', my: 0.2 }} />
                {user?.user_id && (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ fontSize: '0.65rem' }}>📋</Box>
                      <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.7)' }}>User ID: {user.user_id}</Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.15)', my: 0.2 }} />
                  </>
                )}
                <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', ml: 'auto' }}>
                  {user?.is_owner ? 'Owner' : user?.role === 'main_admin' ? 'Admin' : user?.role === 'admin' ? 'Admin' : user?.role === 'sales_engineer' ? 'Sales Engineer' : 'Member'}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
              <Typography sx={{ fontSize: '0.78rem', color: PRIMARY, fontStyle: 'italic', mt: 0.5, mb: 0.5 }}>
                {welcomeMsg}
              </Typography>
            </Box>

            <Divider sx={{ borderColor: 'var(--border)' }} />

            {/* Footer: role + last login */}
            <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ fontSize: '0.85rem' }}>👥</Box>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                  {user?.is_owner ? 'Owner' : user?.role === 'main_admin' ? 'Admin' : user?.role === 'admin' ? 'Admin' : user?.role === 'sales_engineer' ? 'Sales Engineer' : 'Member'}
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  {user?.is_owner ? 'Owner' : user?.role === 'main_admin' ? 'Admin' : user?.role === 'admin' ? 'Admin' : user?.role === 'sales_engineer' ? 'Sales Engineer' : 'Member'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <DotIcon sx={{ fontSize: 10, color: '#22c55e' }} />
                <Typography sx={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                  Last login: {user?.last_login ? (() => {
                    const diff = Date.now() - new Date(user.last_login).getTime();
                    if (diff < 60000) return 'just now';
                    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
                    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
                    return `${Math.floor(diff / 86400000)}d ago`;
                  })() : 'just now'}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ borderColor: 'var(--border)' }} />

            {/* Inline name edit */}
            {editNameOpen && (
              <Box sx={{ px: 2.5, py: 1.5, display: 'flex', gap: 1, alignItems: 'center' }}>
                <input
                  autoFocus
                  value={editNameValue}
                  onChange={e => setEditNameValue(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleUpdateName(); if (e.key === 'Escape') setEditNameOpen(false); }}
                  placeholder="Enter new name"
                  style={{ flex: 1, padding: '6px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: '0.85rem', outline: 'none' }}
                />
                <Button size="small" variant="contained" onClick={handleUpdateName} sx={{ minWidth: 0, px: 1.5, bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DK }, textTransform: 'none', fontSize: '0.78rem', borderRadius: 2 }}>Save</Button>
              </Box>
            )}
            <MenuItem onClick={() => { handleProfileClose(); navigate('/settings'); }} sx={{ py: 1.2, px: 2.5, mx: 1, mt: 0.5, borderRadius: '8px', '&:hover': { bgcolor: 'var(--bg-sidebar-active)' } }}>
              <ListItemIcon><PersonIcon fontSize="small" sx={{ color: 'var(--text-secondary)' }} /></ListItemIcon>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-primary)' }}>Profile</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleProfileClose(); navigate('/settings'); }} sx={{ py: 1.2, px: 2.5, mx: 1, borderRadius: '8px', '&:hover': { bgcolor: 'var(--bg-sidebar-active)' } }}>
              <ListItemIcon><SettingsIcon fontSize="small" sx={{ color: 'var(--text-secondary)' }} /></ListItemIcon>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-primary)' }}>Settings</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleProfileClose(); navigate('/recycle-bin'); }} sx={{ py: 1.2, px: 2.5, mx: 1, borderRadius: '8px', '&:hover': { bgcolor: 'var(--bg-sidebar-active)' } }}>
              <ListItemIcon><RecycleBinIcon fontSize="small" sx={{ color: 'var(--text-secondary)' }} /></ListItemIcon>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-primary)' }}>Recycle Bin</Typography>
            </MenuItem>
            <Divider sx={{ my: 0.5, borderColor: 'var(--border)' }} />
            <MenuItem onClick={handleLogout} sx={{ py: 1.2, px: 2.5, mx: 1, mb: 0.5, borderRadius: '8px', color: 'error.main', '&:hover': { bgcolor: alpha('#ef4444', 0.08) } }}>
              <ListItemIcon><LogoutIcon fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 500 }}>Sign Out</Typography>
            </MenuItem>
          </Menu>

          {/* 3-dot sub-menu */}
          <Menu anchorEl={dotMenuAnchor} open={Boolean(dotMenuAnchor)} onClose={handleDotMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { minWidth: 200, borderRadius: '8px', boxShadow: '0 8px 30px rgba(0,0,0,0.15)', border: '1px solid', borderColor: 'var(--border)', bgcolor: 'var(--bg-surface)' } }}>
            <MenuItem onClick={() => { handleDotMenuClose(); avatarInputRef.current?.click(); }} sx={{ py: 1, px: 2, borderRadius: '6px', mx: 0.5, '&:hover': { bgcolor: 'var(--bg-sidebar-active)' } }}>
              <ListItemIcon><CameraIcon fontSize="small" sx={{ color: 'var(--text-secondary)' }} /></ListItemIcon>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-primary)' }}>Update Profile Picture</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleDotMenuClose(); setEditNameValue(user?.name || ''); setEditNameOpen(true); }} sx={{ py: 1, px: 2, borderRadius: '6px', mx: 0.5, '&:hover': { bgcolor: 'var(--bg-sidebar-active)' } }}>
              <ListItemIcon><EditIcon fontSize="small" sx={{ color: 'var(--text-secondary)' }} /></ListItemIcon>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-primary)' }}>Update Username</Typography>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 }, transition: `width ${TRANSITION}` }}>
        <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)} ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: SIDEBAR_EXPANDED, border: 'none', bgcolor: 'var(--bg-sidebar)' } }}>
          {drawerContent(true)}
        </Drawer>
        <Drawer variant="permanent" open
          sx={{ display: { xs: 'none', md: 'block' }, '& .MuiDrawer-paper': { width: drawerWidth, border: 'none', transition: `width ${TRANSITION}`, overflowX: 'hidden', position: 'fixed', top: 0, left: 0, height: '100vh', zIndex: 1200, bgcolor: 'var(--bg-sidebar)' } }}>
          {drawerContent(false)}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box component="main" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', width: { md: `calc(100% - ${drawerWidth}px)` }, height: '100vh', overflow: 'hidden', bgcolor: 'var(--bg-canvas)', transition: `width ${TRANSITION}` }}>
        <Toolbar sx={{ minHeight: `${HEADER_HEIGHT}px !important`, flexShrink: 0 }} />
        <Box sx={{
          flex: 1,
          overflow: 'auto',
          /* T214 — REDUCED BY EXACTLY THE TABLE GUTTER (12px), because his ruling was
             "the outer card can be expanded a little bit instead of reducing the
             tables width". The cards inside now carry that 12px as inner padding, so
             the table occupies the same pixels it always did and only the CARD grew.
             Changing one of these two without the other turns a spacing fix into a
             column squeeze — which is the thing he explicitly ruled against. */
          px: { xs: '8px', sm: '12px', md: '12px' },
          pt: usesFlushStickyHeader ? 0 : { xs: '10px', sm: '12px', md: '14px' },
          pb: { xs: '14px', sm: '20px', md: '24px' },
        }}>
          {/* animate-fadeIn's transform creates a stacking context that traps
              page-level z-index. position:relative + zIndex:1101 lifts the
              page wrapper above the AppBar (z=1100) so page-level sticky
              headers can render flush over the AppBar's 1px border-bottom. */}
          <Box className="animate-fadeIn" sx={{ width: '100%', position: 'relative', zIndex: 1101 }}><Outlet /></Box>
        </Box>
      </Box>


      {/* T249 — parked with its icon; nothing can open it while the flag is false. */}
      {SHOW_CHAT_AND_AI_ICONS && <AIAssistant open={aiOpen} onClose={() => setAiOpen(false)} />}
    </Box>
  );
};

export default Layout;
